import React from "react";
import { Alert, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, surfaceSx } from "./ownerCenterUi";
import { captureOwnerInboxEntry } from "../../../../services/ownerInbox.service";

export default function OwnerCaptureComposer({ onCreated }) {
  const [text, setText] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [listening, setListening] = React.useState(false);
  const [inputSource, setInputSource] = React.useState("text");
  const [error, setError] = React.useState("");
  const recognitionRef = React.useRef(null);
  const speechSupported = typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  React.useEffect(() => () => recognitionRef.current?.stop?.(), []);

  const toggleVoice = () => {
    if (!speechSupported) return;
    if (listening) { recognitionRef.current?.stop?.(); return; }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new Recognition();
    recognition.lang = "es-AR";
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => { setError(""); setListening(true); };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => { setListening(false); setError("No pude tomar el dictado. Podés escribirlo y guardarlo igual."); };
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results || []).map((result) => result?.[0]?.transcript || "").join(" ").trim();
      if (transcript) { setInputSource("voice"); setText((current) => [current.trim(), transcript].filter(Boolean).join(" ")); }
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const save = async () => {
    const clean = text.trim();
    if (!clean || saving) return;
    setSaving(true); setError("");
    try {
      const response = await captureOwnerInboxEntry(ownerApi, { text: clean, inputSource });
      setText(""); setInputSource("text"); recognitionRef.current = null; onCreated?.(response.entry);
    } catch (err) { setError(formatApiError(err, "No pude guardar esta entrada.")); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ ...surfaceSx, p: { xs: 1.7, md: 2 }, borderColor: "rgba(243,90,58,.2)", boxShadow: "0 16px 46px rgba(243,90,58,.055)" }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} spacing={1.2}>
        <Box sx={{ minWidth: { md: 210 }, maxWidth: { md: 250 } }}>
          <Stack direction="row" spacing={.6} alignItems="center"><Typography sx={{ fontWeight: 1000, fontSize: ".98rem" }}>Entrada rápida</Typography><Chip size="small" label={listening ? "ESCUCHANDO" : "INBOX"} color={listening ? "error" : "default"} sx={{ height: 20, fontSize: ".62rem", fontWeight: 950 }} /></Stack>
          <Typography variant="caption" sx={{ display: "block", mt: .25, color: "text.secondary", fontWeight: 720, lineHeight: 1.4 }}>Decí qué pasó. No hace falta clasificarlo perfecto ahora.</Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={text}
          onChange={(event) => { setInputSource("text"); setText(event.target.value); }}
          placeholder='Ej.: "Mañana pedirle las credenciales de Memorial a Agustín, cinco minutos."'
          onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter") save(); }}
          helperText="Ctrl/⌘ + Enter para guardar · si detectamos fecha, duración o nombres reales, te mostramos una propuesta editable después de capturar."
          FormHelperTextProps={{ sx: { fontWeight: 700, mt: .55 } }}
        />
        <Stack direction="row" spacing={.7} alignItems="center" sx={{ alignSelf: { md: "stretch" } }}>
          <Tooltip title={speechSupported ? (listening ? "Detener dictado" : "Dictar") : "Dictado no disponible en este navegador"}><span><IconButton disabled={!speechSupported} color={listening ? "error" : "primary"} onClick={toggleVoice} sx={{ width: 46, height: 46, border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.11)" }}>{listening ? <StopCircleRoundedIcon /> : <MicRoundedIcon />}</IconButton></span></Tooltip>
          <Button variant="contained" disabled={!text.trim() || saving} onClick={save} endIcon={<SendRoundedIcon />} sx={{ ...compactActionSx, minHeight: 46, px: 2.1, whiteSpace: "nowrap" }}>{saving ? "Guardando" : "Capturar"}</Button>
        </Stack>
      </Stack>
      {error ? <Alert severity="warning" sx={{ mt: 1.1 }}>{error}</Alert> : null}
    </Box>
  );
}
